/**
 * Fleet Performance Table Component
 *
 * Reusable table component that renders ranked vehicle performance data
 * with all 12 columns. Supports sorting by total_point (descending for Top,
 * ascending for Worst) with alphabetical plate_number tie-breaking.
 *
 * Requirements: 2.4, 2.5, 2.6, 2.7, 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 3.8, 3.9, 3.10
 */

import React from "react";
import "./FleetPerformanceTable.scss";

const MAX_ROWS = 5;

/**
 * Formats a numeric value to a specified number of decimal places.
 * Returns "-" if the value is null or undefined.
 */
export const formatValue = (value, decimals) => {
  if (value === null || value === undefined) {
    return "-";
  }
  return Number(value).toFixed(decimals);
};

/**
 * Formats a point value. Returns 0 if the value is null or undefined.
 */
export const formatPoint = (value) => {
  if (value === null || value === undefined) {
    return 0;
  }
  return Math.round(value);
};

/**
 * Formats a string value. Returns "-" if the value is null or undefined.
 */
export const formatString = (value) => {
  if (value === null || value === undefined) {
    return "-";
  }
  return value;
};

/**
 * Sorts vehicles by total_point with alphabetical plate_number tie-breaking.
 *
 * @param {Array} vehicles - Array of VehiclePerformance objects
 * @param {"desc"|"asc"} sortOrder - "desc" for Top table (highest first), "asc" for Worst table (lowest first)
 * @returns {Array} Sorted array (limited to MAX_ROWS)
 */
export const sortVehicles = (vehicles, sortOrder) => {
  if (!vehicles || vehicles.length === 0) {
    return [];
  }

  const sorted = [...vehicles].sort((a, b) => {
    const pointA = a.total_point ?? 0;
    const pointB = b.total_point ?? 0;

    // Primary sort by total_point
    const pointComparison =
      sortOrder === "desc" ? pointB - pointA : pointA - pointB;

    if (pointComparison !== 0) {
      return pointComparison;
    }

    // Tie-breaking: alphabetical by plate_number ascending
    const plateA = (a.plate_number || "").toLowerCase();
    const plateB = (b.plate_number || "").toLowerCase();
    return plateA.localeCompare(plateB);
  });

  return sorted.slice(0, MAX_ROWS);
};

/**
 * Formats a cell value based on its column key.
 */
export const formatCell = (key, value) => {
  switch (key) {
    case "rank":
      return value;
    case "total_point":
      return formatPoint(value);
    case "plate_number":
    case "vehicle_type":
    case "risk_rating_label":
      return formatString(value);
    case "fuel_economy_value":
      return formatValue(value, 2);
    case "fuel_economy_point":
    case "idling_point":
    case "co2_emission_point":
    case "risk_rating_point":
      return formatPoint(value);
    case "idling_percentage":
      return formatValue(value, 1);
    case "co2_emission_value":
      return formatValue(value, 3);
    default:
      return formatString(value);
  }
};

/**
 * FleetPerformanceTable Component
 *
 * @param {Object} props
 * @param {Array} props.vehicles - Array of VehiclePerformance objects
 * @param {string} props.title - Heading displayed above the table
 * @param {"desc"|"asc"} props.sortOrder - "desc" for Top table, "asc" for Worst table
 */
export const FleetPerformanceTable = ({ vehicles = [], title, sortOrder }) => {
  const sortedVehicles = sortVehicles(vehicles, sortOrder);

  return (
    <div className="fleet-performance-table">
      <h3 className="fleet-performance-table__title">{title}</h3>
      <div className="fleet-performance-table__wrapper">
        <table className="fleet-performance-table__table">
          <thead>
            {/* Group header row */}
            <tr className="fleet-performance-table__group-header">
              <th rowSpan={2} className="fleet-performance-table__th--rank">Top<br />Rank</th>
              <th rowSpan={2} className="fleet-performance-table__th--total">Total<br />Point</th>
              <th rowSpan={2} className="fleet-performance-table__th--plate">Plate No.</th>
              <th rowSpan={2} className="fleet-performance-table__th--type">Vehicle Type</th>
              <th colSpan={2} className="fleet-performance-table__th--group">Fuel</th>
              <th colSpan={2} className="fleet-performance-table__th--group">Idling</th>
              <th colSpan={2} className="fleet-performance-table__th--group">CO₂</th>
              <th colSpan={2} className="fleet-performance-table__th--group">Risk Rating</th>
            </tr>
            {/* Sub-header row */}
            <tr className="fleet-performance-table__sub-header">
              <th>Fuel Economy<br />(Km/L)</th>
              <th>Point</th>
              <th>%</th>
              <th>Point</th>
              <th>CO₂ Emissions<br />(mt)</th>
              <th>Point</th>
              <th>Rating</th>
              <th>Point</th>
            </tr>
          </thead>
          <tbody>
            {sortedVehicles.map((vehicle, index) => (
              <tr key={vehicle.plate_number || index}>
                <td className="fleet-performance-table__td--rank">{index + 1}</td>
                <td className="fleet-performance-table__td--total">{formatCell("total_point", vehicle.total_point)}</td>
                <td className="fleet-performance-table__td--plate">{formatCell("plate_number", vehicle.plate_number)}</td>
                <td className="fleet-performance-table__td--type">{formatCell("vehicle_type", vehicle.vehicle_type)}</td>
                <td className="fleet-performance-table__td--numeric">{formatCell("fuel_economy_value", vehicle.fuel_economy_value)}</td>
                <td className="fleet-performance-table__td--numeric">{formatCell("fuel_economy_point", vehicle.fuel_economy_point)}</td>
                <td className="fleet-performance-table__td--numeric">{formatCell("idling_percentage", vehicle.idling_percentage)}</td>
                <td className="fleet-performance-table__td--numeric">{formatCell("idling_point", vehicle.idling_point)}</td>
                <td className="fleet-performance-table__td--numeric">{formatCell("co2_emission_value", vehicle.co2_emission_value)}</td>
                <td className="fleet-performance-table__td--numeric">{formatCell("co2_emission_point", vehicle.co2_emission_point)}</td>
                <td className="fleet-performance-table__td--rating">{formatCell("risk_rating_label", vehicle.risk_rating_label)}</td>
                <td className="fleet-performance-table__td--numeric">{formatCell("risk_rating_point", vehicle.risk_rating_point)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FleetPerformanceTable;
